import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import DashboardNavbar from '@/components/feature/DashboardNavbar';

export default function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
  });

  useEffect(() => {
    async function fetchSettings() {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setSmtpSettings({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_user: data.smtp_user || '',
          smtp_pass: data.smtp_pass || '',
        });
      }
      setLoading(false);
    }

    fetchSettings();
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    const { error } = await supabase
      .from('user_settings')
      .upsert({
        user_id: user.id,
        ...smtpSettings,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      setMessage({ type: 'error', text: 'Ayarlar kaydedilirken bir hata oluştu.' });
    } else {
      setMessage({ type: 'success', text: 'Ayarlar başarıyla kaydedildi.' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardNavbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <DashboardNavbar />
      
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-stone-800">Ayarlar</h1>
          <p className="text-stone-500 text-sm mt-1">Uygulama genelindeki tercihlerinizi yönetin.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="p-6 border-b border-stone-100">
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <i className="ri-mail-send-line text-amber-500"></i>
              E-posta (SMTP) Yapılandırması
            </h2>
            <p className="text-xs text-stone-400 mt-1">
              Görev atamalarında bildirim göndermek için kullanılacak e-posta sunucusu ayarları.
            </p>
          </div>

          <form onSubmit={handleSave} className="p-6 space-y-6">
            {message.text && (
              <div className={`p-4 rounded-xl text-sm ${
                message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'
              }`}>
                {message.text}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-stone-700 mb-1.5">SMTP Host</label>
                <input
                  type="text"
                  value={smtpSettings.smtp_host}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                />
              </div>
              
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-stone-700 mb-1.5">SMTP Port</label>
                <input
                  type="number"
                  value={smtpSettings.smtp_port}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                  placeholder="587"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-stone-700 mb-1.5">E-posta Adresi</label>
                <input
                  type="email"
                  value={smtpSettings.smtp_user}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })}
                  placeholder="ornek@gmail.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Uygulama Şifresi</label>
                <input
                  type="password"
                  value={smtpSettings.smtp_pass}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_pass: e.target.value })}
                  placeholder="••••••••••••••••"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                />
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-4 flex gap-3">
              <i className="ri-information-line text-amber-600 text-lg mt-0.5"></i>
              <div className="text-xs text-amber-800 leading-relaxed">
                <p className="font-semibold mb-1">Gmail kullanıyorsanız:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Google hesabınızda <strong>2 Adımlı Doğrulama</strong> aktif olmalıdır.</li>
                  <li>Normal şifreniz yerine <strong>Uygulama Şifresi</strong> (App Password) oluşturup buraya girmelisiniz.</li>
                  <li>Host: <code>smtp.gmail.com</code>, Port: <code>587</code> olarak ayarlayın.</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-stone-800 text-white rounded-xl text-sm font-semibold hover:bg-stone-900 transition-all shadow-sm disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Ayarları Kaydet
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
